import { useEffect, useState, type FormEvent } from 'react';
import { FIELD_LIMITS } from '@icebreaker/shared';
import { Button, Field, Modal, Textarea, useFormErrors } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { coverRemovalReset, removeOrganizationCover } from '../store/organizations.slice';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  failureKind,
  fieldErrorPaths,
  useStaffName,
} from './dialog-parts';
import styles from './organizations.module.css';

/**
 * KAPAĞI KALDIR (kurum kapağı · Story P1). Gerekçe zorunludur: boşken birincil
 * düğme pasiftir. Gövde personelin gördüğü kapağın kimliğini taşır; sunucu 409
 * dönerse (kapak bu arada değişti ya da zaten kaldırılmış) diyalog kapanır, detay
 * yeniden çekilir ve kart tek cümleyle söyler. Birincil eylem yıkıcı görünümdedir.
 *
 * Yineleme anahtarı yoktur (sözleşme sonlandırma emsali): ikinci gönderim aynı
 * kimliği taşır, kapak zaten boşsa sunucu 409 döner ve ikinci kayıt oluşmaz.
 */
export function RemoveCoverDialog({
  organizationId,
  organizationName,
  coverMediaId,
  onClose,
}: {
  organizationId: string;
  organizationName: string;
  coverMediaId: string;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const staffName = useStaffName();
  const removal = useAppSelector((root: RootState) => root.panelOrgs.coverRemoval);
  const [reason, setReason] = useState('');
  /*
   * Diyalog yalnız KENDİ gönderiminin sonucuyla kapanır. Önceki denemenin 409'u
   * kartın notu için depoda kalır; açılıştaki sıfırlama aynı commit'teki kapanış
   * etkisinden SONRA görünür, bu bayrak olmadan 409 sonrası yeniden açılan diyalog
   * ilk karede kendini kapatıyordu (tech-lead P1).
   */
  const [sent, setSent] = useState(false);

  useEffect(() => {
    dispatch(coverRemovalReset());
  }, [dispatch]);

  const conflict = removal.phase === 'failed' && removal.error?.statusCode === 409;
  useEffect(() => {
    if (sent && (removal.phase === 'succeeded' || conflict)) onClose();
  }, [conflict, onClose, removal.phase, sent]);

  const busy = removal.phase === 'loading';
  const outcome = failureKind(removal.phase, removal.error);
  const serverPaths = fieldErrorPaths(removal.error);
  const serverForm = useFormErrors(removal.error, ['reason']);
  const empty = reason.trim() === '';
  const reasonError = serverPaths.has('reason')
    ? (serverForm.at('reason') ?? t.t('admin.orgs.cover.reasonHint'))
    : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || empty) return;
    setSent(true);
    dispatch(
      removeOrganizationCover.request({ organizationId, coverMediaId, reason: reason.trim() }),
    );
  };

  return (
    <Modal
      open
      title={t.t('admin.orgs.cover.dialogTitle')}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="remove-cover-form"
            loading={busy}
            disabled={!busy && empty}
            loadingLabel={t.t('admin.orgs.cover.submitting')}
          >
            {outcome !== 'none' ? t.t('admin.orgs.retry') : t.t('admin.orgs.cover.submit')}
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub>{t.t('admin.orgs.cover.dialogSub', { name: organizationName })}</DialogSub>
      <form id="remove-cover-form" className={styles.form} onSubmit={submit} noValidate>
        <Field
          id="remove-cover-reason"
          label={t.t('admin.orgs.cover.reason')}
          hint={reasonError ? undefined : t.t('admin.orgs.cover.reasonHint')}
          error={reasonError}
        >
          <Textarea
            id="remove-cover-reason"
            rows={3}
            value={reason}
            maxLength={FIELD_LIMITS.coverRemovalReason.max}
            disabled={busy}
            invalid={Boolean(reasonError)}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <InfoBlock tone="warn">{t.t('admin.orgs.cover.warn')}</InfoBlock>
        {outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedCover')} />
        ) : outcome === 'rejected' ? (
          <ServerBanner>{t.t('admin.orgs.cover.serverError')}</ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}
